import { useState, useEffect } from 'react';
import adminApi from '../../services/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner';
// Shared chrome: Escape closes, and the three management screens look alike
import { AdminModal as Modal, AdminField as Field, AdminCheck as Check } from '../../components/AdminModal';
import { apiErrorMessage } from '../../utils/apiError';
import './AdminUserModals.css';

const GENDERS = ['male', 'female', 'other', 'prefer_not_to_say'];
const TRANSLATIONS = ['ESV', 'NIV', 'NASB', 'KJV', 'NKJV', 'NLT', 'MSG', 'AMP', 'CSB'];
const APP_USAGE_MODES = ['tracking', 'reading', 'both'];

/**
 * Edits one user. Sends only the fields that actually changed, so an admin
 * opening the form and saving without edits is a no-op rather than a rewrite of
 * every column.
 */
export const UserEditModal = ({ user, currentAdminId, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: user.name ?? '',
    email: user.email ?? '',
    age: user.age ?? '',
    gender: user.gender ?? '',
    isActive: user.isActive !== false,
    isAdmin: !!user.isAdmin,
    emailVerified: !!user.emailVerified,
    bibleTranslation: user.bibleTranslation ?? 'ESV',
    appUsageMode: user.appUsageMode ?? 'tracking',
    competitiveMode: !!user.competitiveMode,
    trackReadingDetails: !!user.trackReadingDetails,
    timezone: user.timezone ?? 'America/Chicago',
    reminderEnabled: user.reminderEnabled !== false,
    reminderTime: user.reminderTime ?? '07:00'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isSelf = user.id === currentAdminId;
  const set = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const buildChanges = () => {
    const changes = {};
    const original = {
      name: user.name ?? '',
      email: user.email ?? '',
      age: user.age ?? '',
      gender: user.gender ?? '',
      isActive: user.isActive !== false,
      isAdmin: !!user.isAdmin,
      emailVerified: !!user.emailVerified,
      bibleTranslation: user.bibleTranslation ?? 'ESV',
      appUsageMode: user.appUsageMode ?? 'tracking',
      competitiveMode: !!user.competitiveMode,
      trackReadingDetails: !!user.trackReadingDetails,
      timezone: user.timezone ?? 'America/Chicago',
      reminderEnabled: user.reminderEnabled !== false,
      reminderTime: user.reminderTime ?? '07:00'
    };

    for (const [field, value] of Object.entries(form)) {
      if (value === original[field]) continue;
      if (field === 'age') {
        changes.age = value === '' ? null : Number(value);
      } else if (field === 'gender') {
        changes.gender = value === '' ? null : value;
      } else {
        changes[field] = value;
      }
    }
    return changes;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const changes = buildChanges();
    if (Object.keys(changes).length === 0) {
      onClose();
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await adminApi.updateUser(user.id, changes);
      onSaved(`Updated ${user.email}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this user.'));
    } finally {
      setSaving(false);
    }
  };

  const emailChanged = form.email !== user.email;

  return (
    <Modal
      title={`Edit ${user.name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="user-edit-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <form id="user-edit-form" onSubmit={handleSubmit}>
        <fieldset className="user-modal-group">
          <legend>Account</legend>
          <Field label="Name">
            <input type="text" value={form.name} onChange={set('name')} maxLength={100} required />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={set('email')} required />
          </Field>
          {emailChanged && !form.emailVerified && (
            <p className="user-modal-note">
              Changing the email clears its verified status. Use “Resend verification” afterwards,
              or tick Email verified below to vouch for the new address.
            </p>
          )}
          <div className="user-modal-row">
            <Field label="Age">
              <input type="number" min="1" max="150" value={form.age} onChange={set('age')} />
            </Field>
            <Field label="Gender">
              <select value={form.gender} onChange={set('gender')}>
                <option value="">Not set</option>
                {GENDERS.map((value) => <option key={value} value={value}>{value.replace(/_/g, ' ')}</option>)}
              </select>
            </Field>
          </div>
        </fieldset>

        <fieldset className="user-modal-group">
          <legend>Access</legend>
          <Check
            label="Active"
            checked={form.isActive}
            onChange={set('isActive')}
            disabled={isSelf}
            hint={isSelf ? 'You cannot deactivate your own account.' : 'Inactive users cannot sign in.'}
          />
          <Check
            label="Site admin"
            checked={form.isAdmin}
            onChange={set('isAdmin')}
            disabled={isSelf}
            hint={isSelf ? 'You cannot remove your own admin rights.' : undefined}
          />
          <Check label="Email verified" checked={form.emailVerified} onChange={set('emailVerified')} />
        </fieldset>

        <fieldset className="user-modal-group">
          <legend>Preferences</legend>
          <div className="user-modal-row">
            <Field label="Bible translation">
              <select value={form.bibleTranslation} onChange={set('bibleTranslation')}>
                {TRANSLATIONS.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </Field>
            <Field label="App usage mode">
              <select value={form.appUsageMode} onChange={set('appUsageMode')}>
                {APP_USAGE_MODES.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </Field>
          </div>
          <Check label="Competitive mode" checked={form.competitiveMode} onChange={set('competitiveMode')} />
          <Check label="Track reading details" checked={form.trackReadingDetails} onChange={set('trackReadingDetails')} />
        </fieldset>

        <fieldset className="user-modal-group">
          <legend>Reminders</legend>
          <div className="user-modal-row">
            <Field label="Timezone">
              <input type="text" value={form.timezone} onChange={set('timezone')} placeholder="America/Chicago" />
            </Field>
            <Field label="Reminder time">
              <input type="time" value={form.reminderTime} onChange={set('reminderTime')} />
            </Field>
          </div>
          <Check label="Daily reminders enabled" checked={form.reminderEnabled} onChange={set('reminderEnabled')} />
        </fieldset>
      </form>
    </Modal>
  );
};

export const UserCreateModal = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    age: '',
    gender: '',
    isAdmin: false,
    emailVerified: false,
    sendWelcomeEmail: false
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await adminApi.createUser({
        name: form.name,
        email: form.email,
        password: form.password,
        age: form.age === '' ? null : Number(form.age),
        gender: form.gender === '' ? null : form.gender,
        isAdmin: form.isAdmin,
        emailVerified: form.emailVerified,
        sendWelcomeEmail: form.sendWelcomeEmail
      });
      onSaved(`Created ${form.email}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not create this user.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="New user"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="user-create-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Creating…' : 'Create user'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <form id="user-create-form" onSubmit={handleSubmit}>
        <Field label="Name">
          <input type="text" value={form.name} onChange={set('name')} maxLength={100} required />
        </Field>
        <Field label="Email">
          <input type="email" value={form.email} onChange={set('email')} required />
        </Field>
        <Field label="Temporary password">
          <input type="text" value={form.password} onChange={set('password')} minLength={6} maxLength={255} required />
        </Field>
        <p className="user-modal-note">
          Hand this password to the account holder and ask them to change it after signing in.
        </p>
        <div className="user-modal-row">
          <Field label="Age">
            <input type="number" min="1" max="150" value={form.age} onChange={set('age')} />
          </Field>
          <Field label="Gender">
            <select value={form.gender} onChange={set('gender')}>
              <option value="">Not set</option>
              {GENDERS.map((value) => <option key={value} value={value}>{value.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>
        </div>
        <Check label="Site admin" checked={form.isAdmin} onChange={set('isAdmin')} />
        <Check
          label="Mark email verified"
          checked={form.emailVerified}
          onChange={set('emailVerified')}
          hint="Skips the verification email for an address you already trust."
        />
        <Check label="Send welcome email" checked={form.sendWelcomeEmail} onChange={set('sendWelcomeEmail')} />
      </form>
    </Modal>
  );
};

/**
 * Either sets a password directly or emails a reset link. The direct path exists
 * for phone support; the link path avoids the admin ever knowing the password.
 */
export const UserPasswordModal = ({ user, onClose, onSaved }) => {
  const [mode, setMode] = useState('link');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError(null);
      if (mode === 'link') {
        await adminApi.sendUserPasswordResetLink(user.id);
        onSaved(`Emailed a reset link to ${user.email}`);
      } else {
        await adminApi.resetUserPassword(user.id, password);
        onSaved(`Set a new password for ${user.email}`);
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not reset this password.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Reset password for ${user.name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" form="user-password-form" className="admin-modal-btn is-primary" disabled={saving}>
            {saving ? 'Working…' : mode === 'link' ? 'Send reset link' : 'Set password'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      <form id="user-password-form" onSubmit={handleSubmit}>
        <label className="admin-modal-check">
          <input type="radio" checked={mode === 'link'} onChange={() => setMode('link')} />
          <span>Email a reset link</span>
          <em>The user picks their own password. Valid for one hour.</em>
        </label>
        <label className="admin-modal-check">
          <input type="radio" checked={mode === 'set'} onChange={() => setMode('set')} />
          <span>Set a password now</span>
          <em>Use when you are on the phone with them.</em>
        </label>

        {mode === 'set' && (
          <Field label="New password">
            <input
              type="text"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={6}
              maxLength={255}
              required
            />
          </Field>
        )}
      </form>
    </Modal>
  );
};

/**
 * Shows what a delete would destroy before asking for confirmation, and refuses
 * outright when the user owns a family or church, since those carry NOT NULL
 * owner columns that would make the delete fail anyway.
 */
export const UserDeleteModal = ({ user, onClose, onDeleted }) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadImpact = async () => {
      try {
        const response = await adminApi.getUserImpact(user.id);
        if (!cancelled) setImpact(response.data);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, 'Could not work out what this delete would affect.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadImpact();
    return () => { cancelled = true; };
  }, [user.id]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      await adminApi.deleteUser(user.id);
      onDeleted(`Deleted ${user.email}`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not delete this user.'));
    } finally {
      setDeleting(false);
    }
  };

  const canDelete = impact?.canDelete && confirmation === user.email;

  return (
    <Modal
      title={`Delete ${user.name}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="admin-modal-btn" onClick={onClose} disabled={deleting}>Cancel</button>
          <button
            type="button"
            className="admin-modal-btn is-danger"
            onClick={handleDelete}
            disabled={!canDelete || deleting}
          >
            {deleting ? 'Deleting…' : 'Delete permanently'}
          </button>
        </>
      )}
    >
      {error && <div className="admin-modal-error">{error}</div>}

      {loading ? (
        <LoadingSpinner size="small" message="Checking what this would affect…" />
      ) : impact && (
        <>
          <p className="user-modal-note">
            Deactivating this account instead keeps all of its history and can be undone.
            Deleting cannot.
          </p>

          {impact.blockers.length > 0 && (
            <div className="user-modal-blockers">
              <h4>Reassign these first</h4>
              <ul>
                {impact.blockers.map((blocker) => (
                  <li key={`${blocker.type}-${blocker.id}`}>
                    <strong>{blocker.name}</strong> — {blocker.resolution}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <h4 className="admin-modal-subhead">This would permanently delete</h4>
          <ul className="user-modal-impact">
            {Object.entries(impact.wouldDelete)
              .filter(([, count]) => count > 0)
              .map(([label, count]) => (
                <li key={label}>
                  <span className="impact-count">{count}</span>
                  {label.replace(/([A-Z])/g, ' $1').toLowerCase()}
                </li>
              ))}
          </ul>
          {Object.values(impact.wouldDelete).every((count) => count === 0) && (
            <p className="user-modal-note">This account has no reading history attached.</p>
          )}

          {impact.canDelete && (
            <Field label={`Type ${user.email} to confirm`}>
              <input
                type="text"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="off"
              />
            </Field>
          )}
        </>
      )}
    </Modal>
  );
};
